import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const THEMES = {
  modern_fast_food: {
    id: 'modern_fast_food',
    name: 'Modern Fast Food',
    description: 'Clean and professional dark theme',
    colors: {
      primary: '#111827',
      accent: '#F97316',
      secondary: '#FDBA74',
      background: '#F9FAFB',
      success: '#22C55E',
      surface: '#FFFFFF',
      text: '#111827',
      textMuted: '#6B7280',
      border: '#E5E7EB',
      error: '#EF4444',
    }
  },
  classic_red_yellow: {
    id: 'classic_red_yellow',
    name: 'Classic Red & Yellow',
    description: 'Traditional fast food colors',
    colors: {
      primary: '#18181B',
      accent: '#DC2626',
      secondary: '#FACC15',
      background: '#FFF7ED',
      success: '#16A34A',
      surface: '#FFFFFF',
      text: '#18181B',
      textMuted: '#71717A',
      border: '#E4E4E7',
      error: '#DC2626',
    }
  },
  premium_dark_orange: {
    id: 'premium_dark_orange',
    name: 'Premium Dark Orange',
    description: 'Sophisticated premium look',
    colors: {
      primary: '#0F172A',
      accent: '#EA580C',
      secondary: '#FB923C',
      background: '#F8FAFC',
      success: '#22C55E',
      surface: '#FFFFFF',
      text: '#0F172A',
      textMuted: '#64748B',
      border: '#E2E8F0',
      error: '#EF4444',
    }
  },
  fresh_green_cafe: {
    id: 'fresh_green_cafe',
    name: 'Fresh Green Cafe',
    description: 'Eco-friendly natural feel',
    colors: {
      primary: '#064E3B',
      accent: '#10B981',
      secondary: '#A7F3D0',
      background: '#F0FDF4',
      success: '#16A34A',
      surface: '#FFFFFF',
      text: '#064E3B',
      textMuted: '#047857',
      border: '#D1FAE5',
      error: '#EF4444',
    }
  },
  futuristic_neon: {
    id: 'futuristic_neon',
    name: 'Futuristic Neon',
    description: 'Bold dark mode with neon accents',
    colors: {
      primary: '#020617',
      accent: '#A3E635',
      secondary: '#06B6D4',
      background: '#0F172A',
      success: '#22C55E',
      surface: '#1E293B',
      text: '#F8FAFC',
      textMuted: '#94A3B8',
      border: '#334155',
      error: '#F87171',
    }
  }
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('modern_fast_food');
  const [isLoading, setIsLoading] = useState(true);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('pos_theme');
    if (savedTheme && THEMES[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
    setIsLoading(false);
  }, []);

  // Apply theme CSS variables whenever theme changes
  useEffect(() => {
    const theme = THEMES[currentTheme];
    if (!theme) return;

    const root = document.documentElement;
    const { colors } = theme;

    // Set CSS variables
    root.style.setProperty('--theme-primary', colors.primary);
    root.style.setProperty('--theme-accent', colors.accent);
    root.style.setProperty('--theme-secondary', colors.secondary);
    root.style.setProperty('--theme-background', colors.background);
    root.style.setProperty('--theme-success', colors.success);
    root.style.setProperty('--theme-surface', colors.surface);
    root.style.setProperty('--theme-text', colors.text);
    root.style.setProperty('--theme-text-muted', colors.textMuted);
    root.style.setProperty('--theme-border', colors.border);
    root.style.setProperty('--theme-error', colors.error);

    // Set RGB versions for opacity support
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
    };

    root.style.setProperty('--theme-primary-rgb', hexToRgb(colors.primary));
    root.style.setProperty('--theme-accent-rgb', hexToRgb(colors.accent));
    root.style.setProperty('--theme-secondary-rgb', hexToRgb(colors.secondary));
    root.style.setProperty('--theme-success-rgb', hexToRgb(colors.success));

    // Update body background for dark themes
    if (currentTheme === 'futuristic_neon') {
      document.body.classList.add('dark-theme');
      root.style.setProperty('--theme-icon-invert', '1');
    } else {
      document.body.classList.remove('dark-theme');
      root.style.setProperty('--theme-icon-invert', '0');
    }

  }, [currentTheme]);

  const applyTheme = (themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
      localStorage.setItem('pos_theme', themeId);
    }
  };

  const getTheme = () => THEMES[currentTheme];

  const value = {
    currentTheme,
    theme: THEMES[currentTheme],
    themes: THEMES,
    applyTheme,
    getTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
